import { GATEWAY_URL, TenantConfig } from 'config/tenant.runtime'

/**
 * Client for the front door at app.nexys.com.
 *
 * Deliberately uses plain fetch rather than the shared `Api` helper: `Api` sends every request to
 * the tenant's own backend, and at sign-in we do not yet know which tenant that is. This is the
 * one call in the app that is made before a tenant exists.
 */

/** Returned when one address authenticates against more than one customer. */
export interface TenantChoice {
  chooser: true
  choiceToken: string
  tenants: TenantConfig[]
}

export interface FrontDoorSuccess {
  chooser?: false
  tenant: TenantConfig
  tfa?: boolean
  token?: string
  user?: any
  phone?: string
  hasPasskey?: boolean
  requireReset?: boolean
  requirePwChange?: boolean
  warningPwChange?: boolean
}

export type FrontDoorResponse = TenantChoice | FrontDoorSuccess

export interface FrontDoorLoginParams {
  email: string
  password: string
  /** Set only on the second call, after the user picks from the account chooser. */
  tenantId?: string
  choiceToken?: string
}

export async function frontDoorLogin(params: FrontDoorLoginParams): Promise<FrontDoorResponse> {
  const response = await fetch(`${GATEWAY_URL}/auth/login`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(params),
  })

  const body = await response.json().catch(() => ({}))

  if (!response.ok) {
    // Mirrors what the tenant backends return directly, so the existing error handling in the
    // login page keeps working whether or not the front door is in front of it.
    const error: any = new Error(body?.message || 'Invalid Email or Password!')
    error.response = { status: response.status, data: body }
    throw error
  }

  return body as FrontDoorResponse
}

/** Public branding for the sign-in page, before any tenant is known. */
export async function frontDoorBootstrap(): Promise<{
  tenant: TenantConfig | null
  branding: { title: string; logoUrl?: string | null; primaryColor?: string | null; secondaryColor?: string | null }
} | null> {
  try {
    const response = await fetch(`${GATEWAY_URL}/bootstrap`)
    return response.ok ? await response.json() : null
  } catch {
    // Branding is cosmetic; never block sign-in on it.
    return null
  }
}

/** One "Sign in with ..." button. `ref` is opaque: only the gateway can turn it into a tenant. */
export interface FrontDoorSsoProvider {
  ref: number
  label: string
  vendor: string
}

export interface FrontDoorSsoDiscovery {
  providers: FrontDoorSsoProvider[]
  allowPasswordLogin: boolean
  /** See `SsoProvidersResponse.passwordLoginLimited`; forwarded from the tenant unchanged. */
  passwordLoginLimited?: boolean
  ssoToken: string
}

/**
 * Which SSO buttons an address may use, asked before anyone has authenticated.
 *
 * The reply never names a customer. An address with no SSO and one we have never seen get the
 * same empty answer, and the gateway rate limits this the same way it rate limits sign-in.
 */
export async function frontDoorSsoDiscover(email: string): Promise<FrontDoorSsoDiscovery> {
  const empty: FrontDoorSsoDiscovery = { providers: [], allowPasswordLogin: true, passwordLoginLimited: false, ssoToken: '' }
  try {
    const response = await fetch(`${GATEWAY_URL}/auth/sso/discover`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    if (!response.ok) return empty
    return (await response.json()) as FrontDoorSsoDiscovery
  } catch {
    // Discovery is additive: if it cannot be reached the password form is still there.
    return empty
  }
}

export async function frontDoorSsoStart(params: {
  email: string
  ssoToken: string
  ref: number
  redirectTo: string
}): Promise<{ authorizationUrl?: string }> {
  const response = await fetch(`${GATEWAY_URL}/auth/sso/start`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(params),
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error: any = new Error(body?.message || 'Could not start single sign-on.')
    error.response = { status: response.status, data: body }
    throw error
  }
  return body
}

/** Trades the handoff code for a session, and learns which customer it belongs to. */
export async function frontDoorSsoExchange(params: {
  email: string
  ssoToken: string
  ref: number
  code: string
}): Promise<FrontDoorSuccess> {
  const response = await fetch(`${GATEWAY_URL}/auth/sso/exchange`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(params),
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error: any = new Error(body?.message || 'Single sign-on failed.')
    error.response = { status: response.status, data: body }
    throw error
  }
  return body as FrontDoorSuccess
}
